import React, { useState, useEffect } from 'react';
import { Layers, Plus, Save, Trash2, Edit } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import CategoriesConfig from './CategoriesConfig';
import PublicHolidaysConfig from './PublicHolidaysConfig';

export default function TenantPolicies() {
  const { tenantId } = useAuth();
  const [activeTab, setActiveTab] = useState('leave-types');

  // ... (Leave Types logic remains the same)
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingType, setEditingType] = useState(null);
  
  const defaultLeaveType = {
    name: '',
    balanceSource: 'ANNUAL_QUOTA',
    countingBasis: 'working',
    needsApproval: true,
    maxDays: '',
    requiresAttachment: false
  };

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/tenants/${tenantId}/holidays/leave-types`);
      setLeaveTypes(res.data.data);
    } catch (err) {
      alert('Failed to load leave types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'leave-types') {
      fetchLeaveTypes();
    }
  }, [tenantId, activeTab]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editingType.id) {
        await api.put(`/tenants/${tenantId}/admin/leave-types/${editingType.id}`, editingType);
      } else {
        await api.post(`/tenants/${tenantId}/admin/leave-types`, editingType);
      }
      setEditingType(null);
      fetchLeaveTypes();
    } catch (err) {
      alert('Save failed: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this leave policy? This could affect past requests!')) return;
    try {
      await api.delete(`/tenants/${tenantId}/admin/leave-types/${id}`);
      fetchLeaveTypes();
    } catch (err) {
      alert('Delete failed');
    }
  };

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <Layers size={24} color="var(--primary-500)" />
        <h2 style={{ margin: 0 }}>HR Settings & Policies</h2>
      </div>

      <div className="tabs" style={{ marginBottom: '24px' }}>
        <button className={`tab ${activeTab === 'leave-types' ? 'active' : ''}`} onClick={() => setActiveTab('leave-types')}>Leave Policies</button>
        <button className={`tab ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => setActiveTab('categories')}>Employee Categories</button>
        <button className={`tab ${activeTab === 'public-holidays' ? 'active' : ''}`} onClick={() => setActiveTab('public-holidays')}>Public Holidays</button>
      </div>

      {activeTab === 'leave-types' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button className="btn btn-primary" onClick={() => setEditingType(defaultLeaveType)}>
              <Plus size={16} /> New Policy
            </button>
          </div>

          {editingType && (
            <form onSubmit={handleSave} style={{ background: 'var(--bg-secondary)', padding: '24px', borderRadius: '8px', marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px' }}>{editingType.id ? 'Edit Policy' : 'Create New Policy'}</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Policy Name</label>
                  <input type="text" className="form-input" required value={editingType.name} onChange={e => setEditingType({...editingType, name: e.target.value})} />
                </div>

                <div className="form-group">
                  <label className="form-label">Balance Source</label>
                  <select className="form-input" value={editingType.balanceSource} onChange={e => setEditingType({...editingType, balanceSource: e.target.value})}>
                    <option value="ANNUAL_QUOTA">Annual Quota (Deducts Balance)</option>
                    <option value="OWN_ENTITLEMENT">Own Entitlement (E.g. Maternity)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Counting Basis</label>
                  <select className="form-input" value={editingType.countingBasis} onChange={e => setEditingType({...editingType, countingBasis: e.target.value})}>
                    <option value="working">Working Days (Skips Weekends)</option>
                    <option value="calendar">Calendar Days (Includes Weekends)</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label className="form-label">Max Days Allowed (Optional)</label>
                  <input type="number" className="form-input" value={editingType.maxDays || ''} onChange={e => setEditingType({...editingType, maxDays: e.target.value})} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editingType.needsApproval} onChange={e => setEditingType({...editingType, needsApproval: e.target.checked})} />
                  Needs HR/Manager Approval
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={editingType.requiresAttachment} onChange={e => setEditingType({...editingType, requiresAttachment: e.target.checked})} />
                  Requires File Attachment (e.g., Medical Note)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-outline" onClick={() => setEditingType(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Save size={16} /> Save Policy</button>
              </div>
            </form>
          )}

          {loading ? (
            <p>Loading policies...</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left', color: 'var(--text-muted)' }}>
                  <th style={{ padding: '12px' }}>Policy Name</th>
                  <th style={{ padding: '12px' }}>Balance Source</th>
                  <th style={{ padding: '12px' }}>Counting Basis</th>
                  <th style={{ padding: '12px' }}>Rules</th>
                  <th style={{ padding: '12px', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leaveTypes.map(type => (
                  <tr key={type.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '12px', fontWeight: 500 }}>{type.name}</td>
                    <td style={{ padding: '12px' }}>
                      <span className="status-badge" style={{ background: 'var(--bg-secondary)' }}>
                        {type.balanceSource.replace('_', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '12px', textTransform: 'capitalize' }}>{type.countingBasis}</td>
                    <td style={{ padding: '12px', fontSize: '0.85rem' }}>
                      {type.needsApproval && <div style={{ color: 'var(--accent-amber)' }}>• Needs Approval</div>}
                      {type.requiresAttachment && <div style={{ color: 'var(--primary-500)' }}>• Attachment Req</div>}
                      {type.maxDays && <div>• Max {type.maxDays} Days</div>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button className="btn" style={{ padding: '4px', background: 'none' }} onClick={() => setEditingType(type)}>
                        <Edit size={16} color="var(--text-muted)" />
                      </button>
                      <button className="btn" style={{ padding: '4px', background: 'none' }} onClick={() => handleDelete(type.id)}>
                        <Trash2 size={16} color="var(--accent-rose)" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'categories' && <CategoriesConfig tenantId={tenantId} />}
      {activeTab === 'public-holidays' && <PublicHolidaysConfig tenantId={tenantId} />}
    </div>
  );
}
